import { apiRequest, api } from "./api";
import type {
  FeedingType,
  AppointmentFeedingType,
  NutritionProfile,
  HealthProfile,
  HealthProfileUpdateSource,
  MealData,
  FeedData,
  FeedTime,
  MealStrategy,
  MealStrategyHistory,
  NutritionTarget,
  SpecialFeedConsideration,
  SpecialFeedConsiderationOption,
  NutritionSymptom,
  AppointmentNutritionSymptom,
  MealEntry,
  FeedEntry,
  MealPlan,
  SleepData,
  FoodFrequency,
  DietaryPreference,
  FoodPreferenceItem,
} from "@/types/nutrition";

// =============================================================================
// API Response Types (for paginated responses)
// =============================================================================

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface _FoodItemSearchResult {
  id: number;
  name: string;
  full_name: string;
  food_code: string;
  energy_kcal: string;
  food_item_nutrient_values?: {
    energy: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
}

interface FoodServingResult {
  id: number;
  serving_unit: { code: number; name: string };
  description: string;
  weight_g: string;
  multiplier: string;
  is_default: boolean;
}

interface _FeedItemSearchResult {
  id: number;
  name: string;
  brand?: string;
  calories_per_ml?: number;
}

export type MoveEntryPayload = {
  entry_type: "meal" | "feed";
  source_context: "diet_recall" | "meal_plan";
  target_context: "diet_recall" | "meal_plan";
  source_entry_id: string;
  target_group_id: string;
};

export type MoveTargetContext = "diet_recall" | "meal_plan";

export type MoveTarget = {
  id: string;
  label: string;
  context: MoveTargetContext;
};

export type MoveEntryResponse = {
  success: boolean;
  move_mode: "reassigned" | "created_target";
  old_entry_id: string;
  new_entry_id: string;
};

const withAppointmentParam = (appointmentId: string) => {
  const trimmedAppointmentId = appointmentId.trim();
  return trimmedAppointmentId
    ? { appointment: trimmedAppointmentId }
    : undefined;
};

// =============================================================================
// Feeding Types API
// =============================================================================

export const feedingTypesAPI = {
  // GET /nutrition/feeding-types/ - List all feeding types
  list: async (): Promise<FeedingType[]> => {
    return apiRequest(() => api.get("/nutrition/feeding-types/"));
  },

  // GET /nutrition/{appointmentId}/appointment-feeding-type/ - Get appointment feeding type
  getAppointmentFeedingType: async (
    appointmentId: string
  ): Promise<AppointmentFeedingType | null> => {
    try {
      const response = await apiRequest<AppointmentFeedingType>(() =>
        api.get(`/nutrition/${appointmentId}/appointment-feeding-type/`)
      );
      // Response is a single object or 404 if not found
      return response ?? null;
    } catch {
      return null;
    }
  },

  // POST /nutrition/{appointmentId}/appointment-feeding-type/ - Create appointment feeding type
  setAppointmentFeedingType: async (
    appointmentId: string,
    feedingTypeId: number
  ): Promise<AppointmentFeedingType> => {
    return apiRequest(() =>
      api.post(`/nutrition/${appointmentId}/appointment-feeding-type/`, {
        feeding_type: feedingTypeId,
      })
    );
  },

  // PATCH /nutrition/{appointmentId}/appointment-feeding-type/{id}/ - Update appointment feeding type
  updateAppointmentFeedingType: async (
    appointmentId: string,
    feedingTypeRecordId: string,
    feedingTypeId: number
  ): Promise<AppointmentFeedingType> => {
    return apiRequest(() =>
      api.patch(
        `/nutrition/${appointmentId}/appointment-feeding-type/${feedingTypeRecordId}/`,
        {
          feeding_type: feedingTypeId,
        }
      )
    );
  },
};

// =============================================================================
// Health Profile API (for body measurements, calculated values - BMI, TDEE, BMR, etc.)
// This is the main source of truth for body measurements in nutrition tabs
// =============================================================================

export const healthProfileAPI = {
  // GET /health-profile/?appointment={appointmentId} - Get health profile metrics
  get: async (appointmentId: string): Promise<HealthProfile | null> => {
    try {
      const response = await apiRequest<HealthProfile>(() =>
        api.get(`/health-profile/`, {
          params: withAppointmentParam(appointmentId),
        })
      );
      return response || null;
    } catch (error) {
      console.error("Error fetching health profile:", error);
      return null;
    }
  },

  // POST /health-profile/?appointment={appointmentId} - Update health profile
  // This updates body measurements and recalculates BMI, TDEE, BMR, PA Calories etc.
  update: async (
    appointmentId: string,
    data: {
      weight_in_kgs?: number;
      height_in_cms?: number;
      waist_in_cms?: number;
      hip_in_cms?: number;
      activity_level?: string;
      source?: HealthProfileUpdateSource;
    }
  ): Promise<HealthProfile | null> => {
    try {
      const response = await apiRequest<HealthProfile>(() =>
        api.post(`/health-profile/`, data, {
          params: withAppointmentParam(appointmentId),
        })
      );
      return response || null;
    } catch (error) {
      console.error("Error updating health profile:", error);
      return null;
    }
  },
};

// =============================================================================
// Nutrition Profile API
// =============================================================================

export const nutritionProfileAPI = {
  // GET /nutrition/profile/ - Get nutrition profile for appointment
  get: async (appointmentId: string): Promise<NutritionProfile | null> => {
    try {
      const response = await apiRequest<PaginatedResponse<NutritionProfile>>(
        () =>
          api.get(`/nutrition/profile/`, {
            params: withAppointmentParam(appointmentId),
          })
      );
      return response?.results?.[0] || null;
    } catch {
      return null;
    }
  },

  // POST /nutrition/profile/ - Create nutrition profile
  create: async (data: {
    appointment: string;
    weight?: number;
    height_ft?: number;
    height_in?: number;
    waist?: number;
    hip?: number;
  }): Promise<NutritionProfile> => {
    return apiRequest(() => api.post("/nutrition/profile/", data));
  },

  // PATCH /nutrition/profile/{id}/ - Update nutrition profile
  update: async (
    id: string,
    data: Partial<NutritionProfile>
  ): Promise<NutritionProfile> => {
    return apiRequest(() => api.patch(`/nutrition/profile/${id}/`, data));
  },

  // Sleep Data - Uses patient/{appointmentId}/sleep-pattern/ endpoint (matching Flutter)
  getSleepData: async (appointmentId: string): Promise<SleepData | null> => {
    try {
      const response = await apiRequest<SleepData>(() =>
        api.get(`/patient/${appointmentId}/sleep-pattern/`)
      );
      return response || null;
    } catch {
      return null;
    }
  },

  updateSleepData: async (
    appointmentId: string,
    data: SleepData,
    isNew: boolean = true
  ): Promise<SleepData> => {
    // Helper to convert HH:mm to HH:mm:ss format
    const formatTimeToHHMMSS = (time?: string): string => {
      if (!time) return "00:00:00";
      // If already has seconds, return as is
      if (time.split(":").length === 3) return time;
      // Append :00 for seconds
      return `${time}:00`;
    };

    // Helper to calculate sleep duration from sleep_time and wake_up_time
    const calculateSleepDuration = (
      sleepTime?: string,
      wakeUpTime?: string
    ): string => {
      if (!sleepTime || !wakeUpTime) return "00:00:00";

      // Parse times as minutes from midnight
      const parseTimeToMinutes = (time: string): number => {
        const parts = time.split(":");
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
      };

      const sleepMinutes = parseTimeToMinutes(sleepTime);
      let wakeMinutes = parseTimeToMinutes(wakeUpTime);

      // Handle overnight sleep (e.g., sleep at 23:00, wake at 07:00)
      if (wakeMinutes <= sleepMinutes) {
        wakeMinutes += 24 * 60; // Add 24 hours
      }

      const durationMinutes = wakeMinutes - sleepMinutes;
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;

      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
    };

    // Prepare the payload matching Flutter's format
    const payload = {
      wake_up_time: formatTimeToHHMMSS(data.wake_up_time),
      sleep_time: formatTimeToHHMMSS(data.sleep_time),
      sleep_quality: "good", // Default to good as Flutter does
      sleep_duration: calculateSleepDuration(
        data.sleep_time,
        data.wake_up_time
      ),
      sleep_issues_note: data.sleep_issues_note || "",
      has_sleep_issues: data.has_sleep_issues || false,
    };

    // Use POST for new data, PATCH for existing data (matching Flutter behavior)
    if (isNew) {
      return apiRequest(() =>
        api.post(`/patient/${appointmentId}/sleep-pattern/`, payload)
      );
    }
    return apiRequest(() =>
      api.patch(`/patient/${appointmentId}/sleep-pattern/`, payload)
    );
  },

  // Food Frequency (Poor Food Habits)
  getFoodFrequency: async (
    appointmentId: string
  ): Promise<FoodFrequency | null> => {
    try {
      const response = await apiRequest<FoodFrequency>(() =>
        api.get(`/patient/${appointmentId}/food-habits/`)
      );
      return response || null;
    } catch {
      return null;
    }
  },

  updateFoodFrequency: async (
    appointmentId: string,
    data: FoodFrequency,
    isNew: boolean = true
  ): Promise<FoodFrequency> => {
    if (isNew) {
      return apiRequest(() =>
        api.post(`/patient/${appointmentId}/food-habits/`, data)
      );
    }
    return apiRequest(() =>
      api.patch(`/patient/${appointmentId}/food-habits/`, data)
    );
  },

  // Dietary Preference - Uses patient/{appointmentId}/dietary/ endpoint
  // Canonical regional field is `regional_preferences` (string[]); legacy
  // singular `regional_preference` is retained server-side for back-compat
  // and used as a fallback for older rows.
  getDietaryPreference: async (
    appointmentId: string
  ): Promise<DietaryPreference | null> => {
    try {
      const response = await apiRequest<any>(() =>
        api.get(`/patient/${appointmentId}/dietary/`)
      );
      if (!response) return null;

      const regionalArray: string[] = Array.isArray(
        response.regional_preferences
      )
        ? response.regional_preferences
        : response.regional_preference
          ? [response.regional_preference]
          : [];

      return {
        id: response.id,
        dietary_type: response.preference || response.dietary_type,
        regional_preferences: regionalArray,
      };
    } catch {
      return null;
    }
  },

  updateDietaryPreference: async (
    appointmentId: string,
    data: DietaryPreference,
    isNew: boolean = true
  ): Promise<DietaryPreference> => {
    // Build API payload — only include fields that are being updated so a
    // dietary-type change doesn't clobber regional preferences and vice versa.
    const apiData: Record<string, string | string[]> = {};

    if (data.dietary_type) {
      apiData.preference = data.dietary_type;
    }

    if (data.regional_preferences !== undefined) {
      apiData.regional_preferences = data.regional_preferences;
    }

    if (isNew) {
      // POST creates the row; default dietary type if absent. Empty
      // regional_preferences is a valid "clear all" payload.
      if (!apiData.preference) apiData.preference = "vegetarian";
      if (apiData.regional_preferences === undefined) {
        apiData.regional_preferences = [];
      }
      return apiRequest(() =>
        api.post(`/patient/${appointmentId}/dietary/`, apiData)
      );
    }
    return apiRequest(() =>
      api.patch(`/patient/${appointmentId}/dietary/`, apiData)
    );
  },
};

// =============================================================================
// Food Preference API (Liked/Disliked Food)
// =============================================================================

export const foodPreferenceAPI = {
  // GET /patient/{appointmentId}/food-preference/?liked=true/false
  getPreferences: async (
    appointmentId: string,
    isLiked: boolean
  ): Promise<FoodPreferenceItem[]> => {
    try {
      const response = await apiRequest<FoodPreferenceItem[]>(() =>
        api.get(`/patient/${appointmentId}/food-preference/`, {
          params: { liked: isLiked },
        })
      );
      return response || [];
    } catch {
      return [];
    }
  },

  // POST /patient/{appointmentId}/food-preference/
  addPreference: async (
    appointmentId: string,
    foodItemId: number,
    isLiked: boolean
  ): Promise<FoodPreferenceItem> => {
    return apiRequest(() =>
      api.post(`/patient/${appointmentId}/food-preference/`, {
        food_item_id: foodItemId,
        is_liked: isLiked,
      })
    );
  },

  // DELETE /patient/{appointmentId}/food-preference/{id}/
  removePreference: async (
    appointmentId: string,
    preferenceId: string
  ): Promise<void> => {
    return apiRequest(() =>
      api.delete(`/patient/${appointmentId}/food-preference/${preferenceId}/`)
    );
  },

  // Search food items for adding to preferences
  searchFoodItems: async (
    query: string,
    appointmentId?: string,
    isLiked?: boolean
  ): Promise<
    Array<{
      id: number;
      name: string;
      full_name: string;
      food_code?: string;
    }>
  > => {
    const params: Record<string, string | boolean> = { search: query };
    if (appointmentId) {
      params.appointment_id = appointmentId;
    }
    if (isLiked !== undefined) {
      params.is_liked = isLiked;
    }
    try {
      const response = await apiRequest<
        PaginatedResponse<{
          id: number;
          name: string;
          full_name: string;
          food_code?: string;
        }>
      >(() => api.get("/nutrition/food-items/", { params }));
      return response?.results || [];
    } catch {
      return [];
    }
  },
};

// =============================================================================
// Meal Groups API (Oral Feeding / Diet Recall)
// =============================================================================

/**
 * Get the appropriate entry endpoint based on whether it's meal plan or diet recall
 * Flutter uses:
 * - Diet Recall: nutrition/{appointmentId}/diet-recall-entry/
 * - Meal Plan: nutrition/{appointmentId}/meal-plan-entry/
 */
const getMealEntryEndpoint = (appointmentId: string, isMealPlan: boolean) => {
  return isMealPlan
    ? `/nutrition/${appointmentId}/meal-plan-entry/`
    : `/nutrition/${appointmentId}/diet-recall-entry/`;
};

export const mealGroupsAPI = {
  // GET /nutrition/{appointmentId}/meal-groups/ - Get meal groups for diet recall
  // This is the same endpoint Flutter uses: Apis().mealGroups(appointmentId)
  getDietRecall: async (appointmentId: string): Promise<MealData | null> => {
    try {
      const response = await apiRequest<MealData[]>(() =>
        api.get(`/nutrition/${appointmentId}/meal-groups/`)
      );
      // Response is an array with one item
      return response?.[0] || null;
    } catch {
      return null;
    }
  },

  // GET /nutrition/{appointmentId}/meal-groups/?plan_type=meal_plan&option=OPT1/OPT2/OPT3
  // This is the same endpoint Flutter uses for meal plan
  getMealPlan: async (
    appointmentId: string,
    planIndex: number = 0
  ): Promise<MealData | null> => {
    try {
      // Map planIndex to option: 0 -> OPT1, 1 -> OPT2, 2 -> OPT3
      const option = `OPT${planIndex + 1}`;
      const response = await apiRequest<MealData[]>(() =>
        api.get(`/nutrition/${appointmentId}/meal-groups/`, {
          params: {
            plan_type: "meal_plan",
            option: option,
          },
        })
      );
      // Response is an array with one item
      return response?.[0] || null;
    } catch {
      return null;
    }
  },

  // POST - Add food item to meal (diet recall or meal plan)
  addEntry: async (
    appointmentId: string,
    isMealPlan: boolean,
    data: {
      meal_group: string;
      food_item: number;
      serving_size: number;
      quantity: string;
      order?: number;
    }
  ): Promise<MealEntry> => {
    return apiRequest(() =>
      api.post(getMealEntryEndpoint(appointmentId, isMealPlan), data)
    );
  },

  // POST - Add note to meal (diet recall or meal plan)
  addNote: async (
    appointmentId: string,
    isMealPlan: boolean,
    data: {
      meal_group: string;
      comment: string;
      is_comment: true;
      order?: number;
    }
  ): Promise<MealEntry> => {
    return apiRequest(() =>
      api.post(getMealEntryEndpoint(appointmentId, isMealPlan), data)
    );
  },

  // PATCH - Update meal entry (diet recall or meal plan)
  updateEntry: async (
    appointmentId: string,
    isMealPlan: boolean,
    id: string,
    data: Partial<MealEntry>
  ): Promise<MealEntry> => {
    return apiRequest(() =>
      api.patch(
        `${getMealEntryEndpoint(appointmentId, isMealPlan)}${id}/`,
        data
      )
    );
  },

  // DELETE - Delete meal entry (diet recall or meal plan)
  deleteEntry: async (
    appointmentId: string,
    isMealPlan: boolean,
    id: string
  ): Promise<void> => {
    return apiRequest(() =>
      api.delete(`${getMealEntryEndpoint(appointmentId, isMealPlan)}${id}/`)
    );
  },
};

export const nutritionEntryMoveAPI = {
  moveEntry: async (
    appointmentId: string,
    data: MoveEntryPayload
  ): Promise<MoveEntryResponse> => {
    return apiRequest(() =>
      api.post(`/nutrition/${appointmentId}/move-entry/`, data)
    );
  },
};

// =============================================================================
// Feed Groups API (Tube Feeding)
// Matches Flutter endpoints from apis.dart
// =============================================================================

/**
 * Get the appropriate feed entry endpoint based on whether it's meal plan or diet recall
 * Flutter uses:
 * - Diet Recall: nutrition/{appointmentId}/diet-recall/feed-entry/
 * - Meal Plan: nutrition/{appointmentId}/feed-entry/
 */
const getFeedEntryEndpoint = (appointmentId: string, isMealPlan: boolean) => {
  return isMealPlan
    ? `/nutrition/${appointmentId}/feed-entry/`
    : `/nutrition/${appointmentId}/diet-recall/feed-entry/`;
};

/**
 * Get the appropriate feed group endpoint based on whether it's meal plan or diet recall
 * Flutter uses:
 * - Diet Recall: nutrition/{appointmentId}/diet-recall/feed-groups/
 * - Meal Plan: nutrition/{appointmentId}/feed-groups/?option=OPT1&plan_type=meal_plan
 */
const getFeedGroupEndpoint = (
  appointmentId: string,
  isMealPlan: boolean,
  feedGroupId?: string
) => {
  if (isMealPlan) {
    return feedGroupId
      ? `/nutrition/${appointmentId}/feed-groups/${feedGroupId}/`
      : `/nutrition/${appointmentId}/feed-groups/`;
  }
  return feedGroupId
    ? `/nutrition/${appointmentId}/diet-recall/feed-groups/${feedGroupId}/`
    : `/nutrition/${appointmentId}/diet-recall/feed-groups/`;
};

export const feedGroupsAPI = {
  // GET /nutrition/{appointmentId}/diet-recall/feed-groups/ - Get feed groups for diet recall
  // Matches Flutter: Apis().dietRecallFeedGroups(appointmentId)
  getDietRecall: async (appointmentId: string): Promise<FeedData | null> => {
    try {
      const response = await apiRequest<FeedData>(() =>
        api.get(`/nutrition/${appointmentId}/diet-recall/feed-groups/`)
      );
      return response || null;
    } catch {
      return null;
    }
  },

  // GET /nutrition/{appointmentId}/feed-groups/?option=OPT1&plan_type=meal_plan - Get feed groups for meal plan
  // Matches Flutter: Apis().feedGroupsWithOptions(appointmentId, option, planType)
  getMealPlan: async (
    appointmentId: string,
    planIndex: number = 0
  ): Promise<FeedData | null> => {
    try {
      // Map planIndex to option: 0 -> OPT1, 1 -> OPT2, 2 -> OPT3
      const option = `OPT${planIndex + 1}`;
      const response = await apiRequest<FeedData>(() =>
        api.get(`/nutrition/${appointmentId}/feed-groups/`, {
          params: {
            option: option,
            plan_type: "meal_plan",
          },
        })
      );
      return response || null;
    } catch {
      return null;
    }
  },

  // POST - Create a new feed group
  // Matches Flutter: POST to Apis().feedGroups(appointmentId) or Apis().dietRecallFeedGroups(appointmentId)
  createFeedGroup: async (
    appointmentId: string,
    isMealPlan: boolean,
    data: {
      time: string; // Matches Flutter's CreateFeedGroupRequest which uses 'time'
      option?: string; // OPT1, OPT2, OPT3 for meal plan
      plan_type?: string; // "meal_plan" for meal plan
    }
  ): Promise<FeedTime> => {
    const endpoint = getFeedGroupEndpoint(appointmentId, isMealPlan);
    return apiRequest(() => api.post(endpoint, data));
  },

  // PATCH - Update a feed group
  updateFeedGroup: async (
    appointmentId: string,
    isMealPlan: boolean,
    feedGroupId: string,
    data: { time?: string } // Matches Flutter's UpdateFeedGroupRequest which uses 'time'
  ): Promise<FeedTime> => {
    const endpoint = getFeedGroupEndpoint(
      appointmentId,
      isMealPlan,
      feedGroupId
    );
    return apiRequest(() => api.patch(endpoint, data));
  },

  // DELETE - Delete a feed group
  deleteFeedGroup: async (
    appointmentId: string,
    isMealPlan: boolean,
    feedGroupId: string
  ): Promise<void> => {
    const endpoint = getFeedGroupEndpoint(
      appointmentId,
      isMealPlan,
      feedGroupId
    );
    return apiRequest(() => api.delete(endpoint));
  },

  // POST - Add feed item to a feed group
  // Matches Flutter: POST to Apis().feedEntry(appointmentId) or Apis().dietRecallFeedEntry(appointmentId)
  addEntry: async (
    appointmentId: string,
    isMealPlan: boolean,
    data: {
      feed_group: string;
      feed_item: number;
      volume_ml: string;
      order?: number;
    }
  ): Promise<FeedEntry> => {
    const endpoint = getFeedEntryEndpoint(appointmentId, isMealPlan);
    return apiRequest(() => api.post(endpoint, data));
  },

  // POST - Add note to a feed group
  addNote: async (
    appointmentId: string,
    isMealPlan: boolean,
    data: {
      feed_group: string;
      comment: string;
      is_comment: true;
      order?: number;
    }
  ): Promise<FeedEntry> => {
    const endpoint = getFeedEntryEndpoint(appointmentId, isMealPlan);
    return apiRequest(() => api.post(endpoint, data));
  },

  // PATCH - Update feed entry
  updateEntry: async (
    appointmentId: string,
    isMealPlan: boolean,
    id: string,
    data: Partial<FeedEntry>
  ): Promise<FeedEntry> => {
    const baseEndpoint = getFeedEntryEndpoint(appointmentId, isMealPlan);
    return apiRequest(() => api.patch(`${baseEndpoint}${id}/`, data));
  },

  // DELETE - Delete feed entry
  deleteEntry: async (
    appointmentId: string,
    isMealPlan: boolean,
    id: string
  ): Promise<void> => {
    const baseEndpoint = getFeedEntryEndpoint(appointmentId, isMealPlan);
    return apiRequest(() => api.delete(`${baseEndpoint}${id}/`));
  },
};

// =============================================================================
// Targets API
// =============================================================================

export const nutritionTargetsAPI = {
  // Meal Strategy - GET /nutrition/{appointmentId}/meal-strategy/
  getMealStrategy: async (
    appointmentId: string
  ): Promise<MealStrategy | null> => {
    try {
      const response = await apiRequest<MealStrategy[]>(() =>
        api.get(`/nutrition/${appointmentId}/meal-strategy/`)
      );
      return response?.[0] || null;
    } catch {
      return null;
    }
  },

  // Meal Strategy - POST (create) or PATCH (update)
  updateMealStrategy: async (
    appointmentId: string,
    strategies: string[],
    existingId?: string
  ): Promise<MealStrategy> => {
    const data = { strategies };
    // The user instructed: "we can use this post api for new entry"
    // So we will use POST unconditionally if existingId is missing or if the API dictates it.
    // If the backend accepts POST to create/update the main record:
    if (!existingId || existingId === "") {
      return apiRequest(() =>
        api.post(`/nutrition/${appointmentId}/meal-strategy/`, data)
      );
    }
    // Fallback to PATCH if it truly exists
    return apiRequest(() =>
      api.patch(
        `/nutrition/${appointmentId}/meal-strategy/${existingId}/`,
        data
      )
    );
  },

  // Meal Strategy History - GET with consolidated=true
  getMealStrategyHistory: async (
    appointmentId: string
  ): Promise<MealStrategyHistory[]> => {
    try {
      const response = await apiRequest<MealStrategyHistory[]>(() =>
        api.get(`/nutrition/${appointmentId}/meal-strategy/`, {
          params: { consolidated: true },
        })
      );
      return response || [];
    } catch {
      return [];
    }
  },

  // Nutrition Targets - GET /patient/{appointmentId}/nutrition-targets/
  getNutritionTarget: async (
    appointmentId: string
  ): Promise<NutritionTarget | null> => {
    try {
      const response = await apiRequest<NutritionTarget[]>(() =>
        api.get(`/patient/${appointmentId}/nutrition-targets/`, {
          params: { consolidated: false },
        })
      );
      return response?.[0] || null;
    } catch {
      return null;
    }
  },

  // Nutrition Targets - POST (create) or PATCH (update)
  updateNutritionTarget: async (
    appointmentId: string,
    data: Partial<NutritionTarget>,
    isNew: boolean = false
  ): Promise<NutritionTarget> => {
    if (isNew) {
      return apiRequest(() =>
        api.post(`/patient/${appointmentId}/nutrition-targets/`, data)
      );
    }
    return apiRequest(() =>
      api.patch(`/patient/${appointmentId}/nutrition-targets/`, data)
    );
  },

  // Special Feed Considerations - GET /nutrition/{appointmentId}/meal-plan-special-feed-considerations/
  getSpecialFeedConsiderations: async (
    appointmentId: string
  ): Promise<SpecialFeedConsideration[]> => {
    try {
      const response = await apiRequest<SpecialFeedConsideration[]>(() =>
        api.get(
          `/nutrition/${appointmentId}/meal-plan-special-feed-considerations/`
        )
      );
      return response || [];
    } catch {
      return [];
    }
  },

  // Search special feed considerations - GET /nutrition/special-feed-considerations/?search=
  searchSpecialFeedOptions: async (
    query: string
  ): Promise<SpecialFeedConsiderationOption[]> => {
    if (!query || query.length < 2) return [];
    try {
      const response = await apiRequest<SpecialFeedConsiderationOption[]>(() =>
        api.get(`/nutrition/special-feed-considerations/`, {
          params: { search: query },
        })
      );
      return response || [];
    } catch {
      return [];
    }
  },

  // Update special feed considerations - PUT /nutrition/{appointmentId}/meal-plan-special-feed-considerations/
  updateSpecialFeedConsiderations: async (
    appointmentId: string,
    considerationIds: number[]
  ): Promise<SpecialFeedConsideration[]> => {
    return apiRequest(() =>
      api.put(
        `/nutrition/${appointmentId}/meal-plan-special-feed-considerations/`,
        {
          considerations: considerationIds,
        }
      )
    );
  },
};

// =============================================================================
// Nutrition Symptoms API (uses patient symptoms endpoints matching Flutter)
// =============================================================================

export const nutritionSymptomsAPI = {
  // GET /symptom/?search={query}&appointment_id={appointmentId}&type={type}
  // Search for symptoms (matching Flutter behavior)
  search: async (
    query: string,
    appointmentId: string,
    type: "digestive" | "others"
  ): Promise<NutritionSymptom[]> => {
    try {
      const response = await apiRequest<NutritionSymptom[]>(() =>
        api.get("/symptom/", {
          params: {
            search: query || "",
            appointment_id: appointmentId,
            type: type,
          },
        })
      );
      return response || [];
    } catch {
      return [];
    }
  },

  // GET /symptom/ - List all symptoms (no filtering)
  list: async (): Promise<NutritionSymptom[]> => {
    try {
      const response = await apiRequest<NutritionSymptom[]>(() =>
        api.get("/symptom/")
      );
      return response || [];
    } catch {
      return [];
    }
  },

  // GET /patient/patientsymptoms/{appointmentId}/ - Get appointment symptoms (Flutter style)
  getAppointmentSymptoms: async (
    appointmentId: string
  ): Promise<AppointmentNutritionSymptom[]> => {
    try {
      const response = await apiRequest<AppointmentNutritionSymptom[]>(() =>
        api.get(`/patient/patientsymptoms/${appointmentId}/`)
      );
      return response || [];
    } catch {
      return [];
    }
  },

  // POST /patient/patientsymptoms/ - Add symptom (matching Flutter)
  addSymptom: async (
    appointmentId: string,
    symptomId: number,
    _notes?: string
  ): Promise<AppointmentNutritionSymptom> => {
    return apiRequest(() =>
      api.post("/patient/patientsymptoms/", {
        appointment: appointmentId,
        symptoms: symptomId,
        status: "Ongoing-increased",
      })
    );
  },

  // PATCH /patient/patientsymptoms/{id}/ — soft-delete via the new draft
  // contract. The backend deactivates an AppointmentSymptomsDraftItem when
  // `activity_status` is set to "inactive"; on prescription/meal-plan publish
  // this is propagated to committed PatientSymptoms.
  removeSymptom: async (id: string): Promise<void> => {
    return apiRequest(() =>
      api.patch(`/patient/patientsymptoms/${id}/`, {
        activity_status: "inactive",
      })
    );
  },
};

// =============================================================================
// Meal Plan API
// =============================================================================

export const mealPlanAPI = {
  // GET /nutrition/meal-plans/ - List meal plans
  list: async (appointmentId: string): Promise<MealPlan[]> => {
    try {
      const response = await apiRequest<PaginatedResponse<MealPlan>>(() =>
        api.get(`/nutrition/meal-plans/`, {
          params: withAppointmentParam(appointmentId),
        })
      );
      return response?.results || [];
    } catch {
      return [];
    }
  },

  // POST /nutrition/meal-plans/ - Create meal plan
  create: async (appointmentId: string, name: string): Promise<MealPlan> => {
    return apiRequest(() =>
      api.post("/nutrition/meal-plans/", {
        appointment: appointmentId,
        name,
      })
    );
  },

  // PATCH /nutrition/meal-plans/{id}/ - Update meal plan
  update: async (id: string, data: Partial<MealPlan>): Promise<MealPlan> => {
    return apiRequest(() => api.patch(`/nutrition/meal-plans/${id}/`, data));
  },

  // DELETE /nutrition/meal-plans/{id}/ - Delete meal plan
  delete: async (id: string): Promise<void> => {
    return apiRequest(() => api.delete(`/nutrition/meal-plans/${id}/`));
  },

  // Copy diet recall to meal plan
  copyFromDietRecall: async (
    appointmentId: string,
    planIndex: number
  ): Promise<void> => {
    return apiRequest(() =>
      api.post(`/nutrition/copy-to-meal-plan/`, {
        appointment: appointmentId,
        plan_index: planIndex,
      })
    );
  },
};

// =============================================================================
// Food Search API
// =============================================================================

export const foodSearchAPI = {
  // Search food items
  search: async (
    query: string,
    appointmentId?: string,
    foodType?: string,
    source?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<{
    count: number;
    next: string | null;
    previous: string | null;
    results: Array<{
      id: number;
      name: string;
      full_name: string;
      food_code: string;
      energy_kcal: string;
      source?: string;
      food_type?: string;
      // Read-only. True → log this food in whole servings only.
      requires_whole_servings?: boolean;
      food_item_nutrient_values?: {
        energy: number;
        protein: number;
        carbs: number;
        fat: number;
        fiber: number;
      };
    }>;
  }> => {
    const params: Record<string, string | number> = {
      search: query,
      page,
      page_size: pageSize,
    };
    if (appointmentId) {
      params.appointment_id = appointmentId;
    }
    if (foodType) {
      params.food_type = foodType;
    }
    if (source) {
      params.source = source;
    }
    return apiRequest(() => api.get("/nutrition/food-items/", { params }));
  },

  // Get food item servings
  getServings: async (
    foodItemId: number
  ): Promise<
    Array<{
      id: number;
      serving_unit: { code: number; name: string };
      description: string;
      weight_g: string;
      multiplier: string;
      is_default: boolean;
    }>
  > => {
    const response = await apiRequest<PaginatedResponse<FoodServingResult>>(
      () =>
        api.get("/nutrition/food-servings/", {
          params: { food_item: foodItemId },
        })
    );
    return response?.results || [];
  },
};

// =============================================================================
// Food Types API (segmentation tabs in Add Food dialog)
// =============================================================================

export const foodTypesAPI = {
  list: async (): Promise<Array<{ value: string; label: string }>> => {
    const response = await apiRequest<Array<{ value: string; label: string }>>(
      () => api.get("/nutrition/food-types/")
    );
    return response ?? [];
  },
};

// =============================================================================
// Feed Search API (for Tube Feeding)
// =============================================================================

export const feedSearchAPI = {
  // Search feed items
  search: async (
    query: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<{
    count: number;
    next: string | null;
    previous: string | null;
    results: Array<{
      id: number;
      name: string;
      brand?: string;
      calories_per_ml?: number;
    }>;
  }> => {
    return apiRequest(() =>
      api.get("/nutrition/feed-items/", {
        params: { search: query, page, page_size: pageSize },
      })
    );
  },
};
